
import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, X, Edit2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface EditablePriceCellProps {
  row: { id: string; price: number };
  field: string;
  isPending: (id: string) => boolean;
  applyOptimisticUpdate: (id: string, field: string, value: any) => Promise<void>;
  isOptimisticProcessing: boolean;
  currencySymbol?: string;
}

export function EditablePriceCell({
  row,
  field,
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing,
  currencySymbol = 'USD'
}: EditablePriceCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(row.price.toString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasBuAccess } = useAuth();

  useEffect(() => {
    setValue(row.price.toString());
  }, [row.price]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (hasBuAccess) return; // Prevent saving for BU Access users
    
    const numValue = parseFloat(value);
    
    if (isNaN(numValue) || numValue < 0) {
      setValue(row.price.toString());
      setIsEditing(false);
      return;
    }

    if (numValue === row.price) {
      setIsEditing(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await applyOptimisticUpdate(row.id, field, numValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating price:", error);
      setValue(row.price.toString());
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setValue(row.price.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleClick = () => {
    if (hasBuAccess) return; // Prevent editing for BU Access users
    setIsEditing(true);
  };

  const formatPrice = (price: number): string => {
    if (price === 0) return '0';
    
    // Format: "price currency" (e.g., "250 USD", "820 USDT")
    const formattedNumber = price.toLocaleString();
    return `${formattedNumber} ${currencySymbol}`;
  };

  const pending = isPending(row.id);
  const processing = isSubmitting || pending;

  // For BU Access users, show read-only content
  if (hasBuAccess) {
    return (
      <div className="flex items-center gap-2 px-2 py-1">
        <div className="flex items-center">
          {row.price === 0 ? (
            <Badge variant="outline" className="text-xs">
              No Price Set
            </Badge>
          ) : (
            <span className="font-medium">
              {formatPrice(row.price)}
            </span>
          )}
        </div>
        {processing && (
          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
        )}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <div className="relative">
          <Input
            ref={inputRef}
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="w-24 h-8 text-sm"
            min="0"
            step="0.01"
            disabled={processing}
          />
          {processing && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin" />
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          disabled={processing}
          className="h-6 w-6 p-0"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={processing}
          className="h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center gap-2 group cursor-pointer hover:bg-muted/50 px-2 py-1 rounded"
      onClick={handleClick}
    >
      <div className="flex items-center">
        {row.price === 0 ? (
          <Badge variant="outline" className="text-xs">
            No Price Set
          </Badge>
        ) : (
          <span className="font-medium">
            {formatPrice(row.price)}
          </span>
        )}
      </div>
      {processing ? (
        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
      ) : (
        <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
}

interface PriceCurrencySelectCellProps {
  row: { id: string; currencyType: string };
  field: string;
  options: string[];
  badgeVariant?: "default" | "secondary" | "destructive" | "outline" | "primary" | "dark";
  isPending: (id: string) => boolean;
  applyOptimisticUpdate: (id: string, field: string, value: any) => Promise<void>;
  isOptimisticProcessing: boolean;
}

export function PriceCurrencySelectCell({
  row,
  field,
  options,
  badgeVariant = "default",
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing
}: PriceCurrencySelectCellProps) {
  // Currency is now displayed as part of the price, so this component is simpler
  return (
    <Badge variant={badgeVariant} className="text-xs">
      {row.currencyType || 'USD'}
    </Badge>
  );
}
